import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend
} from 'recharts';
import { Users } from 'lucide-react';

const STATUS_PRIORITY = ['APROBADO', 'PENDIENTE', 'DOCUMENTADO', 'RECHAZADO', 'SIN ESTATUS'];

const STATUS_COLORS = {
  APROBADO: '#22C55E',
  PENDIENTE: '#F59E0B',
  DOCUMENTADO: '#0EA5E9',
  RECHAZADO: '#F43F5E',
  'SIN ESTATUS': '#A56EFF'
};

const FALLBACK_COLORS = ['#00FF66', '#1EEB8A', '#34D399', '#2DD4BF', '#00D4AA', '#F59E0B', '#A56EFF'];
const ROOT_SUPERIOR = 'SIN_SUPERIOR';
const NO_COORDINATOR = 'SIN_COORDINADOR';

const normalizeStatus = (status) => {
  const raw = String(status || '').trim().toUpperCase();
  if (!raw) return 'SIN ESTATUS';

  if (raw.includes('RECHAZ')) return 'RECHAZADO';
  if (raw.includes('DOCUMENT')) return 'DOCUMENTADO';
  if (raw.includes('PEND') || raw.includes('EN APROB')) return 'PENDIENTE';
  if (raw.includes('APROB')) return 'APROBADO';

  return raw;
};

const shortLabel = (value, max = 34) => {
  if (!value) return NO_COORDINATOR;
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}...`;
};

export default function HorasPorAprobadorEstatus({
  rows = [],
  isDarkMode = true
}) {
  const [expandedRows, setExpandedRows] = useState({});

  const cardClasses = isDarkMode
    ? 'bg-[#071a0f]/80 backdrop-blur-md border border-[#00FF66]/20 rounded-xl shadow-lg hover:border-[#00FF66]/40 hover:shadow-[0_0_24px_rgba(0,255,102,0.14)] transition-all duration-300'
    : 'bg-white border border-emerald-200 rounded-xl shadow-lg';

  const textPrimary = isDarkMode ? 'text-[#e8fff0]' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-[#a0c8b0]' : 'text-gray-600';
  const textMuted = isDarkMode ? 'text-[#7ea58f]' : 'text-gray-500';

  const {
    chartData,
    statuses,
    totalHoras,
    totalAprobadores,
    totalSubcoordinadores,
    expandableCount
  } = useMemo(() => {
    const nodeMap = new Map();
    const statusesSet = new Set();

    rows.forEach((item) => {
      const aprobadorDirecto = (item.aprobador_directo || NO_COORDINATOR).trim() || NO_COORDINATOR;
      const aprobadorSuperior = (item.aprobador_superior || ROOT_SUPERIOR).trim() || ROOT_SUPERIOR;
      const estatus = normalizeStatus(item.estatus);
      const horas = Number(item.horas || 0);

      if (!Number.isFinite(horas) || horas <= 0) return;

      if (!nodeMap.has(aprobadorDirecto)) {
        nodeMap.set(aprobadorDirecto, {
          id: aprobadorDirecto,
          aprobador_directo: aprobadorDirecto,
          aprobador_superior: aprobadorSuperior,
          has_parent: false,
          children: [],
          total_horas: 0
        });
      }

      const bucket = nodeMap.get(aprobadorDirecto);
      bucket.aprobador_superior = aprobadorSuperior;
      bucket.total_horas += horas;
      bucket[estatus] = (bucket[estatus] || 0) + horas;
      statusesSet.add(estatus);
    });

    // Si el padre no trae horas en el periodo, lo creamos como nodo virtual
    // para mantener el arbol y ocultar hijos hasta que se expanda.
    Array.from(nodeMap.values()).forEach((node) => {
      const parentId = node.aprobador_superior;
      const validParent = (
        parentId &&
        parentId !== ROOT_SUPERIOR &&
        parentId !== node.id
      );

      if (validParent && !nodeMap.has(parentId)) {
        nodeMap.set(parentId, {
          id: parentId,
          aprobador_directo: parentId,
          aprobador_superior: ROOT_SUPERIOR,
          has_parent: false,
          children: [],
          total_horas: 0
        });
      }
    });

    const parentToChildren = new Map();
    nodeMap.forEach((node) => {
      const parentId = node.aprobador_superior;
      const hasParent = (
        parentId &&
        parentId !== ROOT_SUPERIOR &&
        parentId !== node.id
      );

      node.has_parent = hasParent;

      if (hasParent) {
        if (!parentToChildren.has(parentId)) {
          parentToChildren.set(parentId, []);
        }
        parentToChildren.get(parentId).push(node.id);
      }
    });

    parentToChildren.forEach((children, parentId) => {
      const sortedChildren = children.sort((a, b) => {
        const hoursA = nodeMap.get(a)?.total_horas || 0;
        const hoursB = nodeMap.get(b)?.total_horas || 0;
        return hoursB - hoursA;
      });

      const parentNode = nodeMap.get(parentId);
      if (parentNode) {
        parentNode.children = sortedChildren;
      }
    });

    const statusList = STATUS_PRIORITY
      .filter((status) => statusesSet.has(status))
      .concat(
        Array.from(statusesSet).filter((status) => !STATUS_PRIORITY.includes(status)).sort()
      );

    const aggregateCache = new Map();
    const aggregateNode = (nodeId) => {
      if (aggregateCache.has(nodeId)) {
        return aggregateCache.get(nodeId);
      }

      const node = nodeMap.get(nodeId);
      if (!node) {
        const empty = { total: 0, byStatus: {} };
        aggregateCache.set(nodeId, empty);
        return empty;
      }

      const byStatus = {};
      statusList.forEach((status) => {
        byStatus[status] = Number(node[status] || 0);
      });

      let total = Number(node.total_horas || 0);

      node.children.forEach((childId) => {
        const childAgg = aggregateNode(childId);
        total += Number(childAgg.total || 0);
        statusList.forEach((status) => {
          byStatus[status] += Number(childAgg.byStatus?.[status] || 0);
        });
      });

      const result = { total, byStatus };
      aggregateCache.set(nodeId, result);
      return result;
    };

    const roots = Array.from(nodeMap.values())
      .filter((node) => !node.has_parent)
      .sort((a, b) => {
        const aAgg = aggregateNode(a.id)?.total || 0;
        const bAgg = aggregateNode(b.id)?.total || 0;
        return bAgg - aAgg;
      });

    const visited = new Set();
    const visibleRows = [];

    const pushNode = (nodeId, depth = 0) => {
      if (visited.has(nodeId)) return;
      visited.add(nodeId);

      const node = nodeMap.get(nodeId);
      if (!node) return;

      const aggregate = aggregateNode(nodeId);
      const isGroup = node.children.length > 0;
      const isExpanded = Boolean(expandedRows[nodeId]);

      const rowData = {
        row_id: nodeId,
        aprobador_directo: node.aprobador_directo,
        aprobador_superior: node.aprobador_superior,
        total_horas: Number(aggregate.total || 0),
        depth,
        is_group: isGroup,
        is_expanded: isExpanded,
        aprobador_label: `${'  '.repeat(depth)}${isGroup ? (isExpanded ? '▾ ' : '▸ ') : '• '}${shortLabel(node.aprobador_directo)}`,
        superior_label: node.aprobador_superior === ROOT_SUPERIOR
          ? 'Sin superior'
          : shortLabel(node.aprobador_superior)
      };

      statusList.forEach((status) => {
        rowData[status] = Number(aggregate.byStatus?.[status] || 0);
      });

      visibleRows.push(rowData);

      if (isGroup && isExpanded) {
        node.children.forEach((childId) => {
          pushNode(childId, depth + 1);
        });
      }
    };

    roots.forEach((rootNode) => pushNode(rootNode.id, 0));

    const total = Array.from(nodeMap.values())
      .reduce((acc, node) => acc + Number(node.total_horas || 0), 0);

    const subcoords = Array.from(nodeMap.values())
      .filter((node) => node.has_parent).length;

    const groups = Array.from(nodeMap.values())
      .filter((node) => node.children.length > 0).length;

    return {
      chartData: visibleRows,
      statuses: statusList,
      totalHoras: total,
      totalAprobadores: nodeMap.size,
      totalSubcoordinadores: subcoords,
      expandableCount: groups
    };
  }, [rows, expandedRows]);

  const toggleRow = (row) => {
    if (!row || !row.is_group) return;

    setExpandedRows((prev) => ({
      ...prev,
      [row.row_id]: !prev[row.row_id]
    }));
  };

  const expandAll = () => {
    const next = {};
    chartData.forEach((row) => {
      if (row.is_group) {
        next[row.row_id] = true;
      }
    });
    setExpandedRows(next);
  };

  const collapseAll = () => {
    setExpandedRows({});
  };

  const chartHeight = Math.max(300, chartData.length * 42);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    const totalRow = payload.reduce((sum, item) => sum + Number(item.value || 0), 0);
    const superior = payload[0]?.payload?.aprobador_superior;

    return (
      <div className={`${
        isDarkMode
          ? 'bg-[#03120a]/95 border-2 border-[#00FF66]/40'
          : 'bg-white/95 border-2 border-emerald-300'
      } backdrop-blur-md rounded-lg p-3 shadow-xl min-w-[260px]`}>
        <div className={`${textPrimary} font-semibold text-sm mb-1`}>{label}</div>
        <div className={`${textMuted} text-xs mb-2`}>
          Superior: {superior === ROOT_SUPERIOR ? 'Sin superior' : superior}
        </div>
        {payload.map((entry) => (
          <div key={entry.dataKey} className="flex items-center justify-between gap-3 text-xs mb-1">
            <span className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded" style={{ backgroundColor: entry.color }} />
              <span className={textSecondary}>{entry.dataKey}</span>
            </span>
            <span className={textPrimary}>{Number(entry.value || 0).toFixed(2)} h</span>
          </div>
        ))}
        <div className={`${textPrimary} text-xs font-semibold mt-2 pt-2 border-t ${isDarkMode ? 'border-[#0d3a22]' : 'border-gray-200'}`}>
          Total: {totalRow.toFixed(2)} h
        </div>
      </div>
    );
  };

  return (
    <div className={`${cardClasses} p-4 mt-4`}>
      <h3 className={`${isDarkMode ? 'text-[#00FF66]' : textPrimary} text-lg font-bold mb-1 flex items-center gap-2`}>
        <span
          className="inline-flex items-center justify-center w-7 h-7 rounded-md border"
          style={{
            backgroundColor: isDarkMode ? 'rgba(34,197,94,0.15)' : 'rgba(34,197,94,0.1)',
            borderColor: isDarkMode ? 'rgba(34,197,94,0.35)' : 'rgba(34,197,94,0.35)'
          }}
        >
          <Users width={15} height={15} color="#22C55E" />
        </span>
        Horas por Aprobador y Estatus
      </h3>
      <p className={`${textMuted} text-xs mb-2`}>
        Modo jerarquico: cada coordinador puede desplegar sus subcoordinadores dentro de la misma grafica.
      </p>
      <p className={`${textMuted} text-xs mb-4`}>
        Clic en la barra de un coordinador para expandir o colapsar su desglose.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-[#00FF66]/5 border-[#00FF66]/20' : 'bg-emerald-50 border-emerald-200'}`}>
          <p className={`${textMuted} text-xs uppercase tracking-wide`}>Horas totales</p>
          <p className={`${isDarkMode ? 'text-[#00FF66]' : 'text-emerald-700'} text-xl font-bold`}>
            {totalHoras.toLocaleString('es-MX', { maximumFractionDigits: 2 })}
          </p>
        </div>
        <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-[#00FF66]/5 border-[#00FF66]/20' : 'bg-emerald-50 border-emerald-200'}`}>
          <p className={`${textMuted} text-xs uppercase tracking-wide`}>Aprobadores</p>
          <p className={`${isDarkMode ? 'text-[#00FF66]' : 'text-emerald-700'} text-xl font-bold`}>{totalAprobadores}</p>
        </div>
        <div className={`p-3 rounded-lg border ${isDarkMode ? 'bg-[#00FF66]/5 border-[#00FF66]/20' : 'bg-emerald-50 border-emerald-200'}`}>
          <p className={`${textMuted} text-xs uppercase tracking-wide`}>Subcoordinadores</p>
          <p className={`${isDarkMode ? 'text-[#00FF66]' : 'text-emerald-700'} text-xl font-bold`}>{totalSubcoordinadores}</p>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 mb-3">
        <span className={`${textMuted} text-xs mr-2`}>
          Nodos con desglose: {expandableCount}
        </span>
        <button
          type="button"
          onClick={expandAll}
          className={isDarkMode
            ? 'px-3 py-1 rounded-md text-xs border border-[#00FF66]/30 text-[#9fe8b9] hover:border-[#00FF66]/55 hover:text-white'
            : 'px-3 py-1 rounded-md text-xs border border-emerald-300 text-emerald-700 hover:border-emerald-500'}
        >
          Expandir todo
        </button>
        <button
          type="button"
          onClick={collapseAll}
          className={isDarkMode
            ? 'px-3 py-1 rounded-md text-xs border border-[#00FF66]/30 text-[#9fe8b9] hover:border-[#00FF66]/55 hover:text-white'
            : 'px-3 py-1 rounded-md text-xs border border-emerald-300 text-emerald-700 hover:border-emerald-500'}
        >
          Colapsar todo
        </button>
      </div>

      {chartData.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <div className={`${textMuted} text-sm`}>No hay datos por aprobador para los filtros seleccionados</div>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 10, right: 24, left: 20, bottom: 10 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke={isDarkMode ? '#0e3d25' : '#E5E7EB'} horizontal={false} />
            <XAxis
              type="number"
              stroke={isDarkMode ? '#88b79d' : '#6B7280'}
              tick={{ fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              type="category"
              dataKey="aprobador_label"
              width={270}
              stroke={isDarkMode ? '#88b79d' : '#6B7280'}
              tick={{ fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: isDarkMode ? 'rgba(0,255,102,0.08)' : 'rgba(16,185,129,0.08)' }} />
            <Legend
              wrapperStyle={{
                fontSize: '12px',
                color: isDarkMode ? '#d9d9d9' : '#111827'
              }}
            />
            {statuses.map((status, index) => (
              <Bar
                key={status}
                dataKey={status}
                stackId="horas"
                fill={STATUS_COLORS[status] || FALLBACK_COLORS[index % FALLBACK_COLORS.length]}
                radius={index === statuses.length - 1 ? [0, 6, 6, 0] : [0, 0, 0, 0]}
                onClick={(data) => toggleRow(data?.payload)}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}
